import dayjs from 'dayjs';
import { pdf } from '@react-pdf/renderer';
import { ReportCoverLetter, ReportCoverLetterData } from './report-cover-letter';
import PdfEpicReportInfoPage from '../../../shared/pdf-generation/pdf-pages/pdf-verification-report-info-page';
import { useQuery } from '@apollo/client';
import { Entity, StaffReportCoverLetterContainerEntitiesDocument } from '../../../../generated';
import { ComponentQueryLoader } from '../../../shared/component-query-loader';
import { lookupCountryQuery } from './staff-components-country-query';
import { Country } from '../../../../types/country';

interface ReportCoverLetterContainerProps {}

export interface GenerationalSuffix {
  suffix: string;
}

export const ReportCoverLetterContainer: React.FC<ReportCoverLetterContainerProps> = () => {
  const generationalSuffixOptions = [
    {
      suffix: 'Jr'
    },
    {
      suffix: 'Sr'
    },
    {
      suffix: 'I'
    },
    {
      suffix: 'II'
    },
    {
      suffix: 'III'
    },
    {
      suffix: 'IV'
    }
  ];

  const files = ['Verification Report', 'Verified Credential', 'Credential Translation', 'Verification Form'];

  const { data: entities, loading: entitiesLoading, error: entitiesError } = useQuery(StaffReportCoverLetterContainerEntitiesDocument);

  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(lookupCountryQuery);

  const handleGeneratePdf = async (formData: ReportCoverLetterData): Promise<File> => {
    const sendDestinationEntity = entities?.entities?.find((entity) => entity?.entityName === formData.sendDestination);
    // const issuingInstitutionEntity = entities?.entities?.find((entity) => entity?.entityName === formData.issuingInstitution);

    const combinedData = {
      applicantId: formData.applicantId || formData.email,
      caseId: formData.caseId,
      dateOfBirth: formData.dateOfBirth,
      dateOfIssued: dayjs().format('D MMMM YYYY'),
      nameOnCredential: formData.nameOnCredential,
      gender: formData.gender,
      restOfName: formData.restOfName,
      lastName: formData.lastName,
      photo: formData.photo,
      credentialType: formData.typeOfCredential,
      includedFiles: formData.includedFiles,
      generationalSuffix: formData.generationalSuffix,
      issuingInstitution: formData.issuingInstitution,
      sendDestination: formData.sendDestination,
      sendDestinationAddressLine1: sendDestinationEntity?.client?.address?.addressLine1,
      sendDestinationAddressLine2: sendDestinationEntity?.client?.address?.addressLine2,
      sendDestinationZipCode: sendDestinationEntity?.client?.address?.zipOrPostalCode,
      sendDestinationCity: sendDestinationEntity?.client?.address?.city,
      sendDestinationState: sendDestinationEntity?.client?.address?.stateOrProvince,
      sendDestinationCountry: sendDestinationEntity?.client?.address?.country,
      statusOfCredential: formData.statusOfCredential
    };
    const pdfBlob = await pdf(<PdfEpicReportInfoPage data={combinedData} />).toBlob();
    return new File([pdfBlob], 'cover-letter.pdf', { type: 'application/pdf' });
  };

  return (
    <ComponentQueryLoader
      hasData={entities !== undefined && countryData !== undefined}
      loading={entitiesLoading || countryLoading}
      error={entitiesError || countryError}
      hasDataComponent={
        <ReportCoverLetter
          generatePdf={handleGeneratePdf}
          generationalSuffixOptions={generationalSuffixOptions}
          files={files}
          sendDestinationEntities={entities?.entities?.filter((entity): entity is Entity => entity?.isClient ?? false) ?? ([] as Entity[])}
          issuingInstitutionEntities={entities?.entities?.filter((entity): entity is Entity => entity?.isIssuingInstitution ?? false) ?? ([] as Entity[])}
          countries={countryData?.countries?.eventPayload?.countries as Country[]}
        />
      }
    />
  );
};
